/**
 * Button utilities — pure functions shared by theme.ts and button.tsx.
 * Kept separate to avoid circular imports.
 */

import type { ButtonIntent, ButtonVariant } from './button.types'

/** Matches DEFAULT_BORDER_WIDTH in consts.ts; duplicated here to avoid a circular import. */
const DEFAULT_BORDER_WIDTH_FALLBACK = 1

/** Canonical hierarchy keys recognised by the two-prop API. */
const HIERARCHY_KEYS = new Set<string>(['primary', 'secondary', 'tertiary'])

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Converts a two-prop variant+intent pair into the combined theme key used
 * internally (e.g. `resolveVariantKey('primary','brand') => 'primaryBrand'`).
 * When `intent` is omitted, `'brand'` is used as the default
 * (e.g. `resolveVariantKey('primary') => 'primaryBrand'`).
 *
 * If `variant` is already a fully-qualified legacy key (e.g. `'primaryBrand'`)
 * it is returned unchanged so the new API is backward-compatible.
 *
 * Returns `'primaryBrand'` (the default variant) when `variant` is absent.
 */
export const resolveVariantKey = (
  variant: ButtonVariant | string | undefined,
  intent: ButtonIntent | string | undefined,
): string | undefined => {
  if (!variant) return `primary${capitalize(intent ?? 'brand')}`

  // Already a combined legacy key — pass through unchanged
  if (!HIERARCHY_KEYS.has(variant)) return variant

  // New two-prop API: combine hierarchy + intent (defaults to 'brand' when intent is absent)
  return `${variant}${capitalize(intent ?? 'brand')}`
}

/**
 * Extracts a numeric value from various input types (number, string with px, etc.).
 * Moved here from button.tsx so it can be used and tested independently.
 *
 * @param value   - Value to extract number from
 * @param fallback - Default value if extraction fails
 */
export const extractNumericValue = (value: unknown, fallback = DEFAULT_BORDER_WIDTH_FALLBACK): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    if (!isNaN(parsed)) return parsed
  }
  return fallback
}

/**
 * Wraps a props object so each mapped key becomes `var(--css-var, originalValue)`.
 * Unmapped keys are passed through unchanged.
 * Defined here (not in button.tsx) so it can be imported without pulling in React.
 */
export const wrapWithVars = (
  props: Record<string, unknown>,
  mapping: Record<string, string>,
): Record<string, unknown> => {
  if (!props || Object.keys(props).length === 0) return {}
  const result: Record<string, unknown> = {}
  Object.entries(props).forEach(([key, value]) => {
    if (key in mapping && value !== undefined) {
      result[key] = `var(${mapping[key]}, ${value})`
    } else if (!(key in mapping)) {
      result[key] = value
    }
  })
  return result
}
